import type { Middleware, MiddlewareAPI } from '@reduxjs/toolkit'
import { authClient } from '@/auth/authClient'
import { useAuthStore } from '@/auth/authStore'
import { isAccessTokenStale } from '@/auth/tokenUtils'
import { wsConnected, wsConnecting, wsDisconnected, wsError } from './wsSlice'
import { WsManager } from './wsManager'
import type { WsServerMessage } from './wsTypes'
import type { Query } from './apiTypes'
import { authenticatedWsUrl, WS_BASE_URL, wsUrlForLog } from './endpoints'
import { useStore } from '@/store'
import {
  mergeSliceNodes,
  computeSearchEnvelopeKpisFromNodes,
  computeSnapshotTimeBoundsFromNodes,
  type SearchSliceNode,
  type SearchSliceEnvelope,
} from './searchContract'

const WS_CLOSE_UNAUTH = 4401
const WS_CLOSE_EXPIRED = 4403

let manager: WsManager | null = null
let lastLoggedPath: string | null = null
let activeUrl: string | null = null
let lastCtxKey: string | null = null
let pendingQuery: Query | null = null
let refreshInFlight: Promise<void> | null = null

function stableStringify(obj: unknown) {
  const seen = new WeakSet<object>()
  const norm = (v: unknown): unknown => {
    if (!v || typeof v !== 'object') return v
    if (seen.has(v as object)) return null
    seen.add(v as object)
    if (Array.isArray(v)) return v.map(norm)
    const src = v as Record<string, unknown>
    const out: Record<string, unknown> = {}
    for (const k of Object.keys(src).sort()) out[k] = norm(src[k])
    return out
  }
  return JSON.stringify(norm(obj))
}

function isSliceNode(x: unknown): x is SearchSliceNode {
  if (!x || typeof x !== 'object') return false
  const o = x as Record<string, unknown>
  return 'kind' in o && 'metrics' in o && 'value' in o
}

function targetDrillEnvelope(st: ReturnType<typeof useStore.getState>): boolean {
  return st.view === 'hex' && st.dimension === 'stream' && !!st.drillNode
}

function resolveConnectUrl(baseOverride?: string): string | null {
  const token = useAuthStore.getState().accessToken
  if (!token) return null
  const base = baseOverride ?? WS_BASE_URL
  return authenticatedWsUrl(base, token)
}

async function refreshWsSession(api: MiddlewareAPI): Promise<void> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    try {
      const r = await authClient.refresh()
      useAuthStore.getState().setAuthenticated({
        accessToken: r.access_token,
        expiresIn: r.expires_in,
        user: r.user,
      })
      manager?.disconnect()
      manager = null
      activeUrl = null
      api.dispatch({ type: 'ws/connect' })
    } catch {
      useAuthStore.getState().setAnonymous('session_expired')
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

function handleAuthExpired(api: MiddlewareAPI) {
  void refreshWsSession(api)
}

type ActionLike = { type: string; payload?: unknown }

export const wsMiddleware: Middleware = (api) => (next) => (action) => {
  const a = action as ActionLike
  if (a.type === 'ws/connect') {
    if (isAccessTokenStale()) {
      void refreshWsSession(api)
      return next(action)
    }

    const payload = a.payload as { url?: string } | undefined
    const url = resolveConnectUrl(payload?.url)
    if (!url) {
      console.warn('[ws] connect skipped — no access token')
      return next(action)
    }

    const logPath = wsUrlForLog(url)
    if (lastLoggedPath !== logPath) {
      console.info('[ws] connect', logPath)
      lastLoggedPath = logPath
    }

    if (manager && activeUrl === url) {
      manager.connect()
      return next(action)
    }

    api.dispatch(wsConnecting({ url: logPath }))
    manager?.disconnect()
    activeUrl = url
    manager = new WsManager({
      url,
      onOpen: () => {
        api.dispatch(wsConnected())
        if (pendingQuery) manager?.subscribe(pendingQuery)
      },
      onClose: (ev) => {
        api.dispatch(wsDisconnected())
        if (ev.code === WS_CLOSE_EXPIRED || ev.code === WS_CLOSE_UNAUTH) {
          handleAuthExpired(api)
        }
      },
      onError: () => api.dispatch(wsError({ message: 'WebSocket error' })),
      onMessage: (msg: WsServerMessage) => {
        if (msg.type === 'auth_expired') {
          handleAuthExpired(api)
          return
        }
        if (msg.type === 'error' && msg.code === 'unauthorized') {
          handleAuthExpired(api)
          return
        }

        const now = Date.now()
        api.dispatch({ type: `ws/incoming/${msg.type}`, payload: msg })

        if (msg.type === 'subscribed') {
          // `lastCtxKey` is the stable query string we just subscribed with —
          // must match DataController's activeDataKey (stableStringify(query)).
          if (lastCtxKey) {
            useStore.setState({ wsSubscribedKey: lastCtxKey, nowTs: now })
            useStore.getState().tryReleaseDataGate()
          }
          return
        }

        if (msg.type === 'world') {
          const st = useStore.getState()
          if (st.wsUiPaused) return
          if (!lastCtxKey || lastCtxKey !== st.activeDataKey) return
          useStore.setState({
            nowTs: now,
            lastStreamsUpdateTs: now,
            worldFlowResult: {
              ts: msg.ts,
              fromMs: msg.fromMs,
              toMs: msg.toMs,
              hops: (msg.hops ?? []) as NonNullable<typeof st.worldFlowResult>['hops'],
              links: (msg.links ?? []) as NonNullable<typeof st.worldFlowResult>['links'],
              regionSeverity: msg.regionSeverity,
            },
          })
          return
        }

        if (msg.type === 'snapshot') {
          const st = useStore.getState()
          // Pause merges until REST + matching subscribed for the active key.
          if (st.wsUiPaused) return
          if (!lastCtxKey || lastCtxKey !== st.activeDataKey) return

          const incoming = (msg.nodes ?? []).filter(isSliceNode)
          const cur = targetDrillEnvelope(st) ? st.searchResultDrill : st.searchResult
          const sameScope =
            !!cur &&
            (msg.view === undefined || msg.view === cur.view) &&
            (msg.dimension === undefined || msg.dimension === cur.dimension)
          const base = sameScope ? (cur?.nodes ?? []) : []
          const merged = mergeSliceNodes(base, incoming)
          const kpis = computeSearchEnvelopeKpisFromNodes(merged)
          const nodeBounds = computeSnapshotTimeBoundsFromNodes(merged)
          // Prefer server display window when present — node min/max can stick
          // inside a single agg bucket and not advance on every tick.
          const fromMs =
            typeof msg.fromMs === 'number' && Number.isFinite(msg.fromMs)
              ? msg.fromMs
              : nodeBounds.fromMs
          const toMs =
            typeof msg.toMs === 'number' && Number.isFinite(msg.toMs)
              ? msg.toMs
              : nodeBounds.toMs
          const envelope: SearchSliceEnvelope = {
            ts: msg.ts,
            fromMs,
            toMs,
            view: msg.view ?? cur?.view,
            dimension: msg.dimension ?? cur?.dimension,
            slice: msg.slice ?? cur?.slice,
            nodes: merged,
            ...kpis,
          }
          const key = targetDrillEnvelope(st) ? 'searchResultDrill' : 'searchResult'
          useStore.setState({ nowTs: now, [key]: envelope, lastStreamsUpdateTs: now })
          return
        }

        // Non-data messages (incidents, pong, welcome) must not advance time state.
      },
    })
    manager.connect()
  }

  if (a.type === 'ws/resubscribe') {
    const payload = a.payload as { query: Query }
    if (!manager) api.dispatch({ type: 'ws/connect' })

    const ctxKey = stableStringify(payload.query)
    if (ctxKey === lastCtxKey) return next(action)
    lastCtxKey = ctxKey
    pendingQuery = payload.query

    console.info('[ws] subscribe', {
      view: payload.query.view,
      dimension: payload.query.dimension,
      slice: payload.query.slice,
      streamId: payload.query.filters.streamId,
    })
    manager?.subscribe(payload.query)
  }

  if (a.type === 'ws/disconnect') {
    manager?.disconnect()
    manager = null
    activeUrl = null
    lastCtxKey = null
    pendingQuery = null
    api.dispatch(wsDisconnected())
  }

  return next(action)
}
